import React, { useState, useEffect } from "react"
import Helmet from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"
import { WindowLocation } from '@reach/router';

import { Sun, Moon } from "react-feather"

import Navbar from "./navigation"

import Footer from "./footer"
import SEO, { SEOProps } from "../utils/seo";

import "../style/index.css"
import { ThemeQuery } from "./__generated__/ThemeQuery"
import store from "../utils/store";

export type Theme = { name: string, label: string, icon: JSX.Element };
type LayoutProps = { children: any, front?: boolean, seo: Partial<SEOProps>, navPlaceholder?: boolean, location: WindowLocation;}

export default ({ children, front, seo, navPlaceholder=true, location }: LayoutProps) => {

    const query = useStaticQuery<ThemeQuery>(graphql`
        query ThemeQuery {
            site {
                siteMetadata {
                    icon
                    switchTheme
                    darkmode
                }
            }
        }
    `)

    const themes: Theme[] = [
        {
            name: "theme-light",
            label: "Light Theme",
            icon: <Sun />
        },{
            name: "theme-dark",
            label: "Dark Theme",
            icon: <Moon />
        }
    ]

    const isDarkTheme = query.site.siteMetadata.darkmode;

    const [theme, changeTheme] = useState(isDarkTheme ? 1 : 0);

    useEffect(() => {
        if(localStorage.getItem("theme-v2")) {
            const t = Number(localStorage.getItem("theme-v2"));
            changeTheme(t);
        }
    }, [])

    // Progressive reveal for sections as they enter the viewport.
    useEffect(() => {
        const nodes = Array.from(document.querySelectorAll(".reveal"))

        if (!("IntersectionObserver" in window)) {
            nodes.forEach(n => n.classList.add("is-visible"))
            return
        }

        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("is-visible")
                        observer.unobserve(entry.target)
                    }
                })
            },
            { rootMargin: "0px 0px -12% 0px", threshold: 0.08 }
        )

        nodes.forEach(n => observer.observe(n))

        return () => observer.disconnect()
    }, [location.pathname])

    const switchTheme = () => {
        const next = theme !== themes.length-1 ? theme+1 : 0;
        changeTheme(next);
        localStorage.setItem("theme-v2", `${next}`);
        store.dispatch("theme:change", undefined);
    }


    return (
        <React.Fragment>
            <Head data={query}/>
            <SEO {...seo} />
            <div className={`wrapper ${themes[theme].name}`}>
                <div className="text-color-default bg-bg">
                    <Navbar front={front} navPlaceholder={navPlaceholder} location={location} currentTheme={theme} switchTheme={switchTheme} themes={themes} allowThemeSwitch={query.site.siteMetadata.switchTheme}/>
                    <main>{children}</main>
                    <Footer />
                </div>
            </div>
        </React.Fragment>
    )
}

const Head = ({ data }) => {
    return (
        <Helmet>
            <link
                rel="icon"
                href={data.site.siteMetadata.icon}
                type="image/png"
            />
            {/* Fonts are self-hosted from /fonts (see src/style/fonts.css).
                Preload the two faces used above the fold so swapping stays quick. */}
            <link
                rel="preload"
                as="font"
                type="font/woff2"
                href="/fonts/jost-400-latin-ext.woff2"
                crossOrigin="anonymous"
            />
            <link
                rel="preload"
                as="font"
                type="font/woff2"
                href="/fonts/cormorant-garamond-400-latin-ext.woff2"
                crossOrigin="anonymous"
            />
        </Helmet>
    )
}
